import React, { useEffect, useState, useRef } from "react";
import { Box } from "@material-ui/core";
import { RotationSchemaPermissions } from "../../../../Permissions";
import { useVerticalNav } from "../../../../Contexts/VerticalNavContext";
import {
  DeleteRotationSchema,
  GetAdvanceSearchRotationScheme,
} from "../../../../Services";
import {
  BasicTable,
  CustomPagination,
  CustomPermission,
  CustomTableHeader,
  NoDataFound,
  TableSkeleton,
} from "../../../../Components";
import DeleteDialog from "./DeleteDialog/DeleteDialog";
import TableActions from "./TableActions/TableActions";
import { returnPropsByPermissions } from "../../../../Helper";
import { useSelectedTheme, useTranslate } from "../../../../Hooks";

import { EditOne, TrashOne, UserEdit } from "../../../../assets/icons";
import { useHistory } from "react-router";

function RotationTable({ SelectedColumns, filters, orderByOptions }) {
  const [activeItem, setActiveItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [schemaList, setSchemaList] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const { setAlertBoxContent, setStickyBottomBox } = useVerticalNav();
  const navigate = useHistory();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 25,
  });
  const dropPagination = useRef(null);
  const stickyPagination = useRef(null);
  const [inputValue, setInputValue] = useState(pagination.currentPage);

  const { translate } = useTranslate("NewAgentsRotationCriteria");
  const {
    theme: { palette },
  } = useSelectedTheme();

  const GetOrderedRotationScheme = async () => {
    if (
      returnPropsByPermissions(
        RotationSchemaPermissions.ViewAllRotationSchema.permissionsId
      )
    ) {
      setIsLoading(true);
      const res = await GetAdvanceSearchRotationScheme({
        filterBy: null,
        orderBy: null,
        pageSize: pagination.itemsPerPage,
        pageIndex: pagination.currentPage,
        search: "",
        agentId: "",
        teamIds: [],
        branchIds: [],
        ...filters,
        ...orderByOptions,
      });
      if (res) {
        setSchemaList(res.result);
        if (!totalCount) setTotalCount(res.totalCount); // here
      }
      setIsLoading(false);
    }
  };

  const deleteHandler = async () => {
    setIsLoading(true);
    const res = await DeleteRotationSchema(activeItem.rotationSchemeId);
    if (!(res && res.status && res.status !== 200)) {
      setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate(`delete-schema-success`),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
      GetOrderedRotationScheme();
    } else
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate(`delete-schema-error`),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    setIsLoading(false);
  };

  useEffect(() => {
    GetOrderedRotationScheme();
  }, [pagination.currentPage, filters, orderByOptions]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!stickyPagination.current) return;
          if (entry.isIntersecting) {
            stickyPagination.current.style.display = "none";
          } else {
            stickyPagination.current.style.display = "block";
          }
        });
      },
      {
        root: null, // Default to viewport
        threshold: 1, // Adjust this threshold as needed
      }
    );

    // Start observing the dropPagination element
    if (dropPagination.current) {
      observer.observe(dropPagination.current);
    }

    // Cleanup observer on component unmount
    return () => {
      if (dropPagination.current) {
        observer.unobserve(dropPagination.current);
      }
    };
  });

  useEffect(() => {
    setStickyBottomBox(
      <CustomPermission
        permissionsList={Object.values(RotationSchemaPermissions)}
        permissionsId={
          RotationSchemaPermissions.ViewAllRotationSchema.permissionsId
        }
      >
        <CustomPagination
          hideInMobile
          ref={stickyPagination}
          currentPage={pagination.currentPage}
          inputValue={inputValue}
          setInputValue={setInputValue}
          totalItems={totalCount} ///  fix here
          itemsPerPage={pagination.itemsPerPage}
          onPageChange={(page) =>
            setPagination((prev) => ({ ...prev, currentPage: page }))
          }
          onItemsPerPageChange={(items) =>
            setPagination((prev) => ({ ...prev, itemsPerPage: items }))
          }
          isSticky
          isLoading={isLoading}
        />
      </CustomPermission>
    );

    return () => {
      setStickyBottomBox(null);
    };
  }, [isLoading]);

  return (
    <Box marginTop={"24px"}>
      {isLoading ? (
        <TableSkeleton rowsNum={schemaList?.length || 12} />
      ) : schemaList?.length ? (
        <>
          <BasicTable
            rowHeight={84}
            setActiveItem={setActiveItem}
            setActiveItemOnRowClick={(item) => {
              setActiveItem(item);
            }}
            rowsData={
              schemaList?.map((item) => {
                return {
                  ...item,
                  id: item.rotationSchemeId,
                };
              }) || []
            }
            columns={SelectedColumns.map((column) => ({
              ...column,
              renderHeader: (params) => (
                <CustomTableHeader
                  params={params}
                  title={column.headerName}
                  hideSelect
                  isHiddenFilter
                />
              ),
            }))}
            tableActions={(data) => {
              if (!true || Object.keys(data).length === 0) return null;
              return (
                <TableActions
                  anchorPosition={data.anchorPosition}
                  actions={[
                    {
                      Icon: () => (
                        <UserEdit
                          width="20"
                          height="20"
                          fill={palette.button.tertiaryGray_fg}
                        />
                      ),
                      actionHandler: () => {
                        /* assign-agent*/
                      },
                    },
                    {
                      Icon: () => (
                        <TrashOne
                          width="20"
                          height="20"
                          fill={palette.button.tertiaryGray_fg}
                        />
                      ),
                      actionHandler: () => {
                        setIsModalOpen(true);
                      },
                    },
                    {
                      Icon: () => (
                        <EditOne
                          width="20"
                          height="20"
                          fill={palette.button.tertiaryGray_fg}
                        />
                      ),
                      actionHandler: () => {
                        const params = new URLSearchParams({
                          id: activeItem.rotationSchemeId,
                        });
                        navigate.push(
                          `/home/rotation-criteria/edit-criteria?${params}`
                        );
                      },
                    },
                  ]}
                />
              );
            }}
          />
          <div ref={dropPagination}>
            <CustomPagination
              hideInMobile
              totalItems={totalCount} ///  fix here
              itemsPerPage={pagination.itemsPerPage}
              currentPage={pagination.currentPage}
              inputValue={inputValue}
              setInputValue={setInputValue}
              isLoading={isLoading}
              onPageChange={(page) =>
                setPagination((prev) => ({ ...prev, currentPage: page }))
              }
              onItemsPerPageChange={(items) =>
                setPagination((prev) => ({ ...prev, itemsPerPage: items }))
              }
            />
          </div>
        </>
      ) : (
        <NoDataFound title={translate("no-data")} subTitle="" />
      )}
      <DeleteDialog
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onDeleteAction={() => {
          deleteHandler();
        }}
      />
    </Box>
  );
}

export default RotationTable;
